import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Save, Search, Upload, X } from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { cn } from '../../utils/cn';

/** GET /admin/suppliers/:id/code-map row (migration 132). */
interface CodeMapRow {
  id: string;
  tecDocSupId: number;
  manufacturerNumber: string;
  brand: string | null;
  supplierCode: string | null;
  source: 'AUTO' | 'CATALOGUE' | 'MANUAL' | 'MISS';
  hits: number;
  lastSeenAt: string;
}
interface CodeMapList {
  rows: CodeMapRow[];
  missing: number;
  mapped: number;
}
interface ImportResult {
  rows: number;
  imported: number;
  skipped: { line: number; reason: string }[];
  unknownBrands: string[];
}

type Status = 'MISSING' | 'MAPPED' | 'ALL';

/** i18n keys (adminScreens.codeMap.source.*) — resolved with t() at render time. */
const SOURCE_LABEL_KEY: Record<CodeMapRow['source'], string> = {
  AUTO: 'adminScreens.codeMap.source.auto',
  CATALOGUE: 'adminScreens.codeMap.source.catalogue',
  MANUAL: 'adminScreens.codeMap.source.manual',
  MISS: 'adminScreens.codeMap.source.miss',
};

const STATUS_LABEL_KEY: Record<Status, string> = {
  MISSING: 'adminScreens.codeMap.status.missing',
  MAPPED: 'adminScreens.codeMap.status.mapped',
  ALL: 'adminScreens.codeMap.status.all',
};

const cid = () => crypto.randomUUID();

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * The supplier code map: TecDoc key (brand + manufacturer number) → the supplier's OWN article code.
 *
 * Search finds a supplier's parts by TecDoc key; placing an ORDER needs the supplier's code, which
 * cannot be derived from the number (Auto Partner sells Maxgear 26-2063 as "OF-3270"). The map fills
 * itself when the search can prove a code; what it cannot prove is listed here as MISSING — a part
 * the supplier lists but whose code we don't know, most-searched first — for someone to fill in, or
 * for the supplier's catalogue file to answer in bulk.
 */
export function CodeMapSheet({
  supplier,
  onClose,
}: {
  supplier: { id: string; name: string };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [status, setStatus] = useState<Status>('MISSING');
  const [q, setQ] = useState('');
  const [data, setData] = useState<CodeMapList | null>(null);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<ImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const r = await apiClient.get<{ data: CodeMapList }>(
        `/admin/suppliers/${supplier.id}/code-map`,
        { params: { status, q: q.trim() || undefined, limit: 300 } }
      );
      setData(r.data.data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const h = setTimeout(() => void load(), q ? 250 : 0);
    return () => clearTimeout(h);
  }, [status, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (row: CodeMapRow): Promise<void> => {
    const code = (drafts[row.id] ?? '').trim();
    if (!code) return;
    setSaving(row.id);
    try {
      await apiClient.put(
        `/admin/suppliers/${supplier.id}/code-map`,
        { tecDocSupId: row.tecDocSupId, number: row.manufacturerNumber, brand: row.brand, code },
        { headers: { 'X-Command-Id': cid() } }
      );
      setDrafts((d) => {
        const next = { ...d };
        delete next[row.id];
        return next;
      });
      push({ tone: 'success', message: `${row.manufacturerNumber} → ${code}` });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(null);
    }
  };

  const importFile = async (file: File | null): Promise<void> => {
    if (!file) return;
    setImporting(true);
    setImported(null);
    try {
      const r = await apiClient.post<{ data: ImportResult }>(
        `/admin/suppliers/${supplier.id}/code-map/import`,
        { fileBase64: await fileToBase64(file) },
        { headers: { 'X-Command-Id': cid() }, timeout: 120_000 }
      );
      setImported(r.data.data);
      push({
        tone: r.data.data.skipped.length > 0 ? 'warning' : 'success',
        title: t('adminScreens.codeMap.importedTitle'),
        message: t('adminScreens.codeMap.importedMessage', {
          count: r.data.data.imported,
          total: r.data.data.rows,
        }),
      });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg border bg-card shadow-lg">
        <div className="flex items-start justify-between gap-4 border-b px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold">
              {t('adminScreens.codeMap.title', { name: supplier.name })}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t('adminScreens.codeMap.intro', { name: supplier.name })}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b px-5 py-3">
          {(['MISSING', 'MAPPED', 'ALL'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={cn(
                'rounded-md border px-2.5 py-1 text-xs font-medium',
                status === s ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-accent'
              )}
            >
              {t(STATUS_LABEL_KEY[s])}
              {data && s !== 'ALL' && (
                <span className="ml-1.5 tabular-nums text-muted-foreground">
                  {s === 'MISSING' ? data.missing : data.mapped}
                </span>
              )}
            </button>
          ))}
          <div className="relative ml-auto w-56">
            <Search className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
            <Input
              className="h-8 pl-8"
              placeholder={t('adminScreens.codeMap.searchPlaceholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.txt,.xls,.xlsx"
            className="hidden"
            onChange={(e) => {
              void importFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <Button
            size="sm"
            variant="outline"
            disabled={importing}
            title={t('adminScreens.codeMap.importTitle')}
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="h-4 w-4" />
            {importing ? t('adminScreens.codeMap.importing') : t('adminScreens.codeMap.import')}
          </Button>
        </div>

        {imported && (
          <div className="border-b bg-muted/40 px-5 py-2 text-xs">
            <Trans
              i18nKey="adminScreens.codeMap.summary"
              values={{ count: imported.imported, total: imported.rows }}
              components={{ b: <b /> }}
            />
            {imported.skipped.length > 0 && (
              <>
                {' · '}
                {t('adminScreens.codeMap.skipped', {
                  count: imported.skipped.length,
                  line: imported.skipped[0].line,
                  reason: imported.skipped[0].reason,
                })}
              </>
            )}
            {imported.unknownBrands.length > 0 && (
              <>
                {' · '}
                {t('adminScreens.codeMap.unknownBrands', {
                  brands: imported.unknownBrands.slice(0, 8).join(', '),
                })}
              </>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card text-left text-xs text-muted-foreground">
              <tr className="border-b">
                <th className="px-5 py-2 font-medium">{t('adminScreens.codeMap.columns.brand')}</th>
                <th className="px-2 py-2 font-medium">
                  {t('adminScreens.codeMap.columns.manufacturerNumber')}
                </th>
                <th className="px-2 py-2 text-right font-medium">
                  {t('adminScreens.codeMap.columns.searched')}
                </th>
                <th className="px-2 py-2 font-medium">
                  {t('adminScreens.codeMap.columns.supplierCode')}
                </th>
                <th className="px-5 py-2" />
              </tr>
            </thead>
            <tbody>
              {data?.rows.map((r) => {
                const draft = drafts[r.id];
                const editing = draft !== undefined;
                return (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="px-5 py-1.5">
                      {r.brand ?? '—'}{' '}
                      <span className="text-xs text-muted-foreground">#{r.tecDocSupId}</span>
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">{r.manufacturerNumber}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                      {r.hits}
                    </td>
                    <td className="px-2 py-1.5">
                      {r.supplierCode && !editing ? (
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-xs">{r.supplierCode}</span>
                          <Badge variant="secondary">{t(SOURCE_LABEL_KEY[r.source])}</Badge>
                        </span>
                      ) : (
                        <Input
                          className="h-8 font-mono text-xs"
                          placeholder={t('adminScreens.codeMap.codePlaceholder')}
                          value={draft ?? ''}
                          onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === 'Enter' && void save(r)}
                        />
                      )}
                    </td>
                    <td className="px-5 py-1.5 text-right">
                      {r.supplierCode && !editing ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDrafts((d) => ({ ...d, [r.id]: r.supplierCode ?? '' }))}
                        >
                          {t('adminScreens.codeMap.change')}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          disabled={saving === r.id || !(draft ?? '').trim()}
                          onClick={() => void save(r)}
                        >
                          <Save className="h-4 w-4" />
                          {t('adminScreens.codeMap.save')}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && data?.rows.length === 0 && (
            <p className="px-5 py-8 text-center text-sm text-muted-foreground">
              {status === 'MISSING'
                ? t('adminScreens.codeMap.emptyMissing')
                : t('adminScreens.codeMap.emptyMapped')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
